import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const statusConfig = {
  active: { label: { fr: 'Actif', ar: 'نشط' }, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  trial: { label: { fr: 'Essai', ar: 'تجريبي' }, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  suspended: { label: { fr: 'Suspendu', ar: 'معلق' }, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  inactive: { label: { fr: 'Inactif', ar: 'غير نشط' }, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

const subscriptionConfig = {
  basic: { label: 'Plan Basique', color: 'bg-muted text-muted-foreground' },
  standard: { label: 'Plan Standard', color: 'bg-primary/10 text-primary' },
  premium: { label: 'Plan Premium', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

const AgencyCard = ({ agency, onEdit, onDelete, currentLanguage = 'fr' }) => {
  const [showCredentials, setShowCredentials] = useState(false);

  const status = statusConfig?.[agency?.status] || statusConfig?.active;
  const subscription = subscriptionConfig?.[agency?.subscription] || subscriptionConfig?.standard;

  const createdAt = agency?.createdAt
    ? new Date(agency.createdAt)?.toLocaleDateString(currentLanguage === 'ar' ? 'ar-MA' : 'fr-FR')
    : '—';

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm hover:shadow-warm-md transition-all duration-200 flex flex-col">
      {/* Card Header */}
      <div className="p-5 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Building2" size={22} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground truncate">{agency?.agencyName || '—'}</h3>
              <p className="text-sm text-muted-foreground truncate">{agency?.email || '—'}</p>
            </div>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${status?.color}`}>
            {status?.label?.[currentLanguage] || status?.label?.fr}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 flex-1 space-y-3">
        {agency?.contactName && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="User" size={15} className="text-muted-foreground flex-shrink-0" />
            <span className="text-foreground">{agency?.contactName}</span>
          </div>
        )}
        {agency?.phone && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Phone" size={15} className="text-muted-foreground flex-shrink-0" />
            <span className="text-foreground">{agency?.phone}</span>
          </div>
        )}
        {agency?.city && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="MapPin" size={15} className="text-muted-foreground flex-shrink-0" />
            <span className="text-foreground">{agency?.city}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Calendar" size={15} className="text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">{currentLanguage === 'fr' ? 'Créé le' : 'تاريخ الإنشاء'}: {createdAt}</span>
        </div>

        {/* Subscription badge */}
        <div className="pt-1">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${subscription?.color}`}>
            {subscription?.label}
          </span>
        </div>

        {/* Credentials toggle */}
        {agency?.tempPassword && (
          <div className="pt-1">
            <button
              onClick={() => setShowCredentials(!showCredentials)}
              className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <Icon name={showCredentials ? 'EyeOff' : 'Eye'} size={13} />
              {currentLanguage === 'fr' ? 'Identifiants de connexion' : 'بيانات تسجيل الدخول'}
            </button>
            {showCredentials && (
              <div className="mt-2 p-3 rounded-lg bg-muted border border-border text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={12} className="text-muted-foreground" />
                  <span className="font-mono text-foreground">{agency?.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Lock" size={12} className="text-muted-foreground" />
                  <span className="font-mono text-foreground">{agency?.tempPassword}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card Footer */}
      <div className="p-4 border-t border-border flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Pencil"
          iconPosition="left"
          onClick={() => onEdit(agency)}
          className="flex-1"
        >
          {currentLanguage === 'fr' ? 'Modifier' : 'تعديل'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="Trash2"
          onClick={() => onDelete(agency)}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        />
      </div>
    </div>
  );
};

export default AgencyCard;
